import React, { useEffect, useState } from 'react';
import { getThreads, getCustomers, ThreadDTO, CustomerBasic } from '../lib/api';
import {
    Loader,
    AlertCircle,
    User,
    Phone,
    Mail,
    MessageSquare,
    Database,
} from 'lucide-react';

const DatosBacked: React.FC = () => {
    const [threads, setThreads] = useState<ThreadDTO[]>([]);
    const [customers, setCustomers] = useState<CustomerBasic[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [threadsData, customersData] = await Promise.all([
                    getThreads({ limit: 10 }),
                    getCustomers(0, 10)
                ]);
                setThreads(threadsData);
                setCustomers(customersData);
            } catch (err: any) {
                console.error("Error fetching data:", err);
                setError(err.message || 'Error desconocido al cargar datos');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 h-screen-minus-header">
                <Loader className="w-8 h-8 animate-spin text-primary mb-2 opacity-50" />
                <p className="text-secondary opacity-70">Cargando datos del backend...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="o-card p-6 border-l-4 border-red-500 bg-red-50 dark:bg-red-900/10 m-4">
                <div className="flex items-start gap-4">
                    <AlertCircle className="text-red-600 dark:text-red-400 mt-1" />
                    <div>
                        <h3 className="font-bold text-red-700 dark:text-red-300">Error al cargar datos</h3>
                        <p className="text-sm mt-1 text-red-600/80 dark:text-red-300/80">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="o-layout-s space-y-8 p-4 md:p-8 animate-fadeIn">
            <header className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                <div>
                   <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-white">
                       <Database className="w-6 h-6 text-indigo-500" />
                       Datos Backend
                   </h1>
                   <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
                       Vista directa de los datos en PostgreSQL via FastAPI
                   </p>
                </div>
            </header>

            {/* Sección Threads */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Últimos Threads ({threads.length})
                    </h2>
                </div>
                
                {threads.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">No hay threads recientes</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 dark:bg-gray-700/30 text-gray-500 font-medium">
                                <tr>
                                    <th className="px-6 py-3">ID</th>
                                    <th className="px-6 py-3">Canal / Teléfono</th>
                                    <th className="px-6 py-3">Estado</th>
                                    <th className="px-6 py-3 text-right">Actualización</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {threads.map((thread) => (
                                    <tr key={thread.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors">
                                        <td className="px-6 py-3 font-mono text-xs text-gray-400">
                                            {thread.id.substring(0, 8)}...
                                        </td>
                                        <td className="px-6 py-3 font-medium text-gray-900 dark:text-white">
                                            {thread.telefono || 'Desconocido'}
                                        </td>
                                        <td className="px-6 py-3">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border
                                                ${thread.status === 'open' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900' : 
                                                  thread.status === 'closed' ? 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700' :
                                                  'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-900' }
                                            `}>
                                                {thread.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-right text-gray-500 text-xs">
                                            {new Date(thread.last_message_at).toLocaleDateString()}
                                            <span className="ml-1 opacity-50">{new Date(thread.last_message_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Sección Customers */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                 <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Clientes Recientes ({customers.length})
                    </h2>
                </div>

                {customers.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">No hay clientes recientes</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-y md:divide-y-0 md:gap-px bg-gray-100 dark:bg-gray-700">
                        {customers.map((customer) => (
                            <div key={customer.id} className="bg-white dark:bg-gray-800 p-4 hover:z-10 relative group transition-all">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded bg-indigo-50 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold uppercase text-xs">
                                        {customer.nombre ? customer.nombre.substring(0, 2) : 'CN'}
                                    </div>
                                    <div className="overflow-hidden min-w-0">
                                        <h3 className="font-medium truncate text-sm text-gray-900 dark:text-white" title={customer.nombre || ''}>
                                            {customer.nombre || 'Sin Nombre'}
                                        </h3>
                                    </div>
                                </div>
                                <div className="pl-11 space-y-1.5">
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <Phone className="w-3 h-3 opacity-70" />
                                        <span className="truncate">{customer.telefono || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <Mail className="w-3 h-3 opacity-70" />
                                        <span className="truncate">{customer.email || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DatosBacked;
